import React, { useState, useEffect } from 'react';
import {
    Box, Button, Card, CardContent, CircularProgress,
    Alert, Snackbar, Typography, Grid, Divider,
    FormControl, InputLabel, Select, MenuItem, FormHelperText,
    Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, InputAdornment
} from '@mui/material';
import { Save, Sync } from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';
import JsonViewTab from './common/JsonViewTab';
import AiGenerateButton from './common/AiGenerateButton';

// Configuration Constants
const TEXT_PROVIDERS = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'google', label: 'Google' },
    { value: 'cohere', label: 'Cohere' },
    { value: 'mistral', label: 'Mistral' },
    { value: 'meta', label: 'Meta (Llama)' },
];

const PROVIDER_MODELS = {
    openai: [
        { value: 'gpt-4o', label: 'GPT-4o (Omni)' },
        { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
        { value: 'gpt-4', label: 'GPT-4' },
        { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    ],
    anthropic: [
        { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
        { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
        { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
    ],
    google: [
        { value: 'gemini-1.5-pro-latest', label: 'Gemini 1.5 Pro' },
        { value: 'gemini-1.5-flash-latest', label: 'Gemini 1.5 Flash' },
        { value: 'gemini-pro', label: 'Gemini Pro' },
    ],
    cohere: [
        { value: 'command-r-plus', label: 'Command R+' },
        { value: 'command-r', label: 'Command R' },
        { value: 'command-light', label: 'Command Light' },
    ],
    mistral: [
        { value: 'mistral-large-latest', label: 'Mistral Large' },
        { value: 'mistral-medium-latest', label: 'Mistral Medium' },
        { value: 'mistral-small-latest', label: 'Mistral Small' },
    ],
    meta: [
        { value: 'llama-3-70b-instruct', label: 'Llama 3 70B' },
        { value: 'llama-3-8b-instruct', label: 'Llama 3 8B' },
    ]
};

const IMAGE_PROVIDERS = [
    { value: 'openai', label: 'OpenAI (DALL-E 3)' },
    { value: 'stabilityai', label: 'Stability AI (SDXL)' },
    { value: 'amazon', label: 'Amazon (Titan)' },
];

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const EdenAIConfigView = () => {
    const [config, setConfig] = useState({
        defaultProvider: '',
        defaultModel: '',
        imageProvider: '',
        pricing: {},
        adminProvider: '',
        adminModel: '',
        modelsMetadata: null,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [tabValue, setTabValue] = useState(0);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.getEdenAIConfig();
            // Ensure pricing is object
            if (!data.pricing) data.pricing = {};
            setConfig(data);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
            setSnackbar({ open: true, message: errorInfo.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updated = await adminApi.updateEdenAIConfig(config);
            if (!updated.pricing) updated.pricing = {};
            setConfig(updated);
            setSnackbar({ open: true, message: 'EdenAI configuration updated successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Update failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field) => (event) => {
        const newValue = event.target.value;

        setConfig(prev => {
            const newConfig = { ...prev, [field]: newValue };

            // If provider changes, reset model to the first available option for that provider
            if (field === 'defaultProvider') {
                const models = newConfig.modelsMetadata?.textProviders?.[newValue]?.models || PROVIDER_MODELS[newValue] || [];
                if (models.length > 0) {
                    newConfig.defaultModel = models[0].value;
                } else {
                    newConfig.defaultModel = '';
                }
            }

            // If admin provider changes, reset admin model to the first available option
            if (field === 'adminProvider') {
                const models = newConfig.modelsMetadata?.textProviders?.[newValue]?.models || PROVIDER_MODELS[newValue] || [];
                if (models.length > 0) {
                    newConfig.adminModel = models[0].value;
                } else {
                    newConfig.adminModel = '';
                }
            }

            return newConfig;
        });
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handlePricingChange = (modelId, type, value) => {
        setConfig(prev => ({
            ...prev,
            pricing: {
                ...prev.pricing,
                [modelId]: {
                    ...prev.pricing[modelId] || {},
                    [type]: value
                }
            }
        }));
    };

    // Prepare list of ALL models for the pricing table
    // Use synced data if available, otherwise fall back to constants
    const allTextModels = config.modelsMetadata?.textProviders
        ? Object.entries(config.modelsMetadata.textProviders).flatMap(([provider, data]) =>
            data.models.map(model => ({ ...model, provider }))
          )
        : Object.keys(PROVIDER_MODELS).flatMap(provider =>
            PROVIDER_MODELS[provider].map(model => ({ ...model, provider }))
          );

    // For images, use synced data if available
    const allImageModels = config.modelsMetadata?.imageProviders
        ? Object.entries(config.modelsMetadata.imageProviders).flatMap(([provider, data]) =>
            data.models.map(model => ({
                value: model.value,
                label: model.label || model.value,
                provider: provider
            }))
          )
        : IMAGE_PROVIDERS.map(p => ({ value: p.value, label: p.label, provider: 'image' }));

    const handleAiPricingSuccess = (generatedPricing) => {
        // Merge generated pricing with existing
        setConfig(prev => ({
            ...prev,
            pricing: {
                ...prev.pricing,
                ...generatedPricing
            }
        }));
        setSnackbar({ open: true, message: 'Pricing data generated!', severity: 'success' });
    };

    const handleSync = async () => {
        setSyncing(true);
        setError(null);
        try {
            const result = await adminApi.syncEdenAIModels();
            
            // Update config with synced data
            if (result.modelsMetadata) {
                setConfig(prev => ({
                    ...prev,
                    modelsMetadata: result.modelsMetadata,
                    pricing: result.pricing || prev.pricing,
                }));
            }
            
            const summary = result.summary || {};
            const message = `Sync completed! ${summary.textModels || 0} text models, ${summary.imageProviders || 0} image providers, ${summary.pricingEntries || 0} pricing entries.`;
            setSnackbar({ open: true, message: message, severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
            setSnackbar({ open: true, message: `Sync failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Helper function to get models from synced metadata or fallback to constants
    const getProviderModels = (provider) => {
        if (config.modelsMetadata?.textProviders?.[provider]?.models) {
            return config.modelsMetadata.textProviders[provider].models;
        }
        return PROVIDER_MODELS[provider] || [];
    };

    // Get text providers from synced data or fallback to constants
    const getTextProviders = () => {
        if (config.modelsMetadata?.textProviders) {
            return Object.entries(config.modelsMetadata.textProviders).map(([key, data]) => ({
                value: key,
                label: data.label || key.charAt(0).toUpperCase() + key.slice(1)
            }));
        }
        return TEXT_PROVIDERS;
    };

    // Get image providers from synced data or fallback to constants
    const getImageProviders = () => {
        if (config.modelsMetadata?.imageProviders) {
            return Object.entries(config.modelsMetadata.imageProviders).map(([key, data]) => ({
                value: key,
                label: data.label || key.charAt(0).toUpperCase() + key.slice(1)
            }));
        }
        return IMAGE_PROVIDERS;
    };

    const textProviders = getTextProviders();
    const imageProviders = getImageProviders();
    const availableModels = getProviderModels(config.defaultProvider);
    const availableAdminModels = getProviderModels(config.adminProvider);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Manage AI providers, models, and pricing settings.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error.message}
                </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="edenai config tabs">
                    <Tab label="Configuration" />
                    <Tab label="Pricing" />
                    <Tab label="JSON" />
                </Tabs>
            </Box>

            {/* TAB 1: Main Configuration */}
            <CustomTabPanel value={tabValue} index={0}>
                <Card>
                    <CardContent>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Text Generation Settings
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="provider-select-label">Default Provider</InputLabel>
                                    <Select
                                        labelId="provider-select-label"
                                        value={config.defaultProvider}
                                        label="Default Provider"
                                        onChange={handleChange('defaultProvider')}
                                    >
                                        {textProviders.map((provider) => (
                                            <MenuItem key={provider.value} value={provider.value}>
                                                {provider.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>Select the primary text generation provider</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth disabled={!config.defaultProvider}>
                                    <InputLabel id="model-select-label">Default Model</InputLabel>
                                    <Select
                                        labelId="model-select-label"
                                        value={config.defaultModel}
                                        label="Default Model"
                                        onChange={handleChange('defaultModel')}
                                    >
                                        {availableModels.map((model) => (
                                            <MenuItem key={model.value} value={model.value}>
                                                {model.label}
                                            </MenuItem>
                                        ))}
                                        {availableModels.length === 0 && (
                                            <MenuItem value={config.defaultModel || "custom"}>
                                                {config.defaultModel || "No models available for this provider"}
                                            </MenuItem>
                                        )}
                                    </Select>
                                    <FormHelperText>Select the specific model to use</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    Image Generation Settings
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="image-provider-select-label">Image Provider</InputLabel>
                                    <Select
                                        labelId="image-provider-select-label"
                                        value={config.imageProvider}
                                        label="Image Provider"
                                        onChange={handleChange('imageProvider')}
                                    >
                                        {imageProviders.map((provider) => (
                                            <MenuItem key={provider.value} value={provider.value}>
                                                {provider.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>Select the provider for image generation</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    Admin LLM Settings
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Configure separate AI provider/model for Admin Portal features (translations, pricing generation, etc.)
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="admin-provider-select-label">Admin Provider</InputLabel>
                                    <Select
                                        labelId="admin-provider-select-label"
                                        value={config.adminProvider}
                                        label="Admin Provider"
                                        onChange={handleChange('adminProvider')}
                                    >
                                        {textProviders.map((provider) => (
                                            <MenuItem key={provider.value} value={provider.value}>
                                                {provider.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>Provider for admin AI features</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth disabled={!config.adminProvider}>
                                    <InputLabel id="admin-model-select-label">Admin Model</InputLabel>
                                    <Select
                                        labelId="admin-model-select-label"
                                        value={config.adminModel}
                                        label="Admin Model"
                                        onChange={handleChange('adminModel')}
                                    >
                                        {availableAdminModels.map((model) => (
                                            <MenuItem key={model.value} value={model.value}>
                                                {model.label}
                                            </MenuItem>
                                        ))}
                                        {availableAdminModels.length === 0 && (
                                            <MenuItem value={config.adminModel || "custom"}>
                                                {config.adminModel || "No models available for this provider"}
                                            </MenuItem>
                                        )}
                                    </Select>
                                    <FormHelperText>Model for admin AI features</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={syncing ? <CircularProgress size={20} /> : <Sync />}
                                        onClick={handleSync}
                                        disabled={syncing || saving}
                                    >
                                        {syncing ? 'Syncing...' : 'Sync Models & Pricing'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                                        onClick={handleSave}
                                        disabled={saving || syncing}
                                    >
                                        {saving ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                </Box>
                                {config.modelsMetadata && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Last synced: {new Date(config.modelsMetadata.lastSynced).toLocaleString()}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </CustomTabPanel>

            {/* TAB 2: Pricing */}
            <CustomTabPanel value={tabValue} index={1}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <AiGenerateButton
                        onSuccess={handleAiPricingSuccess}
                        buttonLabel="Generate Pricing Data"
                        systemMessage="You are an expert in AI model pricing. Output ONLY valid JSON."
                        promptContext={`Please generate estimated market pricing (as of 2024/25) for the following models. 
                        Models: ${allTextModels.map(m => m.value).join(', ')}, ${allImageModels.map(m => m.value).join(', ')}.
                        Format: { "model_id": { "input": 0.00001, "output": 0.00003 } } (prices in USD). 
                        For image models use { "image": 0.04 }.
                        Use reasonable defaults if exact price unknown.`}
                        jsonMode={true}
                    />
                </Box>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="pricing table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Provider</TableCell>
                                <TableCell>Model</TableCell>
                                <TableCell align="right">Input Price (USD)</TableCell>
                                <TableCell align="right">Output/Image Price (USD)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Text Models */}
                            <TableRow>
                                <TableCell colSpan={4} sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Text Models</TableCell>
                            </TableRow>
                            {allTextModels.map((row) => (
                                <TableRow key={row.value}>
                                    <TableCell>{row.provider}</TableCell>
                                    <TableCell component="th" scope="row">
                                        {row.label} ({row.value})
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={config.pricing?.[row.value]?.input || ''}
                                            onChange={(e) => handlePricingChange(row.value, 'input', e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            }}
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={config.pricing?.[row.value]?.output || ''}
                                            onChange={(e) => handlePricingChange(row.value, 'output', e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            }}
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {/* Image Models */}
                            <TableRow>
                                <TableCell colSpan={4} sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Image Models</TableCell>
                            </TableRow>
                            {allImageModels.map((row) => (
                                <TableRow key={row.value}>
                                    <TableCell>{row.provider}</TableCell>
                                    <TableCell component="th" scope="row">
                                        {row.label}
                                    </TableCell>
                                    <TableCell align="right">
                                        -
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Per Image"
                                            value={config.pricing?.[row.value]?.image || ''}
                                            onChange={(e) => handlePricingChange(row.value, 'image', e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            }}
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving Pricing...' : 'Save All Changes'}
                    </Button>
                </Box>
            </CustomTabPanel>

            {/* TAB 3: JSON */}
            <CustomTabPanel value={tabValue} index={2}>
                <JsonViewTab
                    data={config.pricing}
                    onSave={(newData) => setConfig(prev => ({ ...prev, pricing: newData }))}
                />
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving JSON...' : 'Save All Changes'}
                    </Button>
                </Box>
            </CustomTabPanel>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
        </Box>
    );
};

export default EdenAIConfigView;
